import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useCreateTask, useUpdateTask } from "@/hooks/tasks/useTasks";
import { useQueryClient } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { EntitySearchPopover } from "@/components/calendar/EntitySearchPopover";
import { Checkbox } from "@/components/ui/checkbox";
import { CalendarIcon, X, CheckSquare2, Users, Briefcase, ClipboardList, Puzzle, CalendarDays, Plus, Search, Link, FileText, Trash2, History } from "lucide-react";
import { TaskHistoryTab } from "./TaskHistoryTab";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useModal } from "@/contexts/ModalContext";
import { AssigneeSelector } from "@/components/candidates/AssigneeSelector";
import { useAuth } from "@/providers/AuthProvider";
import { useUsers } from "@/hooks/useUsers";
import { useTaskLinkedUsers } from "@/hooks/useLinkedUsers";
import { fetchCustomFields, CustomField, CustomFieldsJSON } from "@/integrations/supabase/customFieldsApi";
import { matchesCandidateSearch, matchesJobSearch } from "@/utils/searchUtils";
import { AddCandidateSelectionModal } from "@/components/candidates/AddCandidateSelectionModal";
import { AddCandidateForm } from "@/components/candidates/AddCandidateForm";
import { AddCompanyForm } from "@/components/companies/AddCompanyForm";
import { JobOffersAddModal } from "@/components/jobs/JobOffersAddModal";
import { CandidateFormData } from "@/utils/candidate";
import { Candidate } from "@/types/candidate";
import { useAddCompany } from "@/hooks/useAddCompany";
import { useAddJob } from "@/hooks/jobs/useAddJob";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@radix-ui/react-popover";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { MentionDropdown } from "@/components/comments/MentionDropdown";

// Register custom Mention format to preserve user ID
const Inline = Quill.import('blots/inline');

class MentionBlot extends Inline {
	static create(userId: string) {
		const node = super.create() as HTMLElement;
		node.setAttribute('data-mention-id', userId);
		node.classList.add('user-mention');
		return node;
	}

	static formats(node: HTMLElement) {
		const mentionId = node.getAttribute('data-mention-id');
		return mentionId ? mentionId : undefined;
	}
}

MentionBlot.blotName = 'mention';
MentionBlot.tagName = 'span';
MentionBlot.className = 'user-mention';

Quill.register('formats/mention', MentionBlot);

interface CreateTaskFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: () => void;
  initialData?: any;
  isEditing?: boolean;
  customHeaderActions?: React.ReactNode;
}

export const CreateTaskForm = ({ isOpen, onClose, onSubmit, initialData, isEditing = false, customHeaderActions }: CreateTaskFormProps) => {
  const { t } = useTranslation();
  const { openModal, closeModal } = useModal();
  const { userProfile, currentOrganization } = useAuth();
  const organizationId = currentOrganization?.id;
  const { data: users = [] } = useUsers();
  const { data: dbLinkedUsers = [], isLoading: linkedUsersLoading } = useTaskLinkedUsers(isEditing && initialData?.id ? initialData.id : undefined);
  const createTaskMutation = useCreateTask();
  const updateTaskMutation = useUpdateTask();
  const queryClient = useQueryClient();
  const isLoading = createTaskMutation.isPending || updateTaskMutation.isPending;
  const [fields, setFields] = useState<CustomField[]>([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [fieldsError, setFieldsError] = useState<string | null>(null);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>({});
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    status: "todo" as 'todo' | 'inProgress' | 'done',
    priority: "medium" as 'low' | 'medium' | 'high' | 'urgent',
    due_date: null as Date | null,
    user_id: "",
    assigned_to: ""
  });

  const [linkedUsers, setLinkedUsers] = useState<string[]>([]);
  const linkedUsersInitialized = useRef(false);
  const linkedUsersModified = useRef(false);

  const [relations, setRelations] = useState({
    candidates: [] as string[],
    matches: [] as string[],
    jobs: [] as string[],
    companies: [] as string[],
    calendar: [] as string[]
  });

  // Store pre-linked candidate info for display
  const [prelinkedCandidate, setPrelinkedCandidate] = useState<{id: string, name: string} | null>(null);
  
  // Store pre-linked company info for display
  const [prelinkedCompany, setPrelinkedCompany] = useState<{id: string, name: string} | null>(null);
  
  // Store pre-linked job info for display
  const [prelinkedJob, setPrelinkedJob] = useState<{id: string, title: string} | null>(null);
  
  // Store pre-linked match info for display
  const [prelinkedMatch, setPrelinkedMatch] = useState<{id: string} | null>(null);

  const [activeTab, setActiveTab] = useState<'general' | 'custom-fields' | 'history'>('general');
  const [mobileTab, setMobileTab] = useState<'general' | 'relations'>('general');
  
  // Track unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const initialFormData = useRef<any>(null);
  const initialCustomFieldValues = useRef<Record<string, any>>({});
  
  // Unified search state for EntitySearchPopover
  const [candidateSearchQuery, setCandidateSearchQuery] = useState('');
  const [candidateSearchResults, setCandidateSearchResults] = useState<any[]>([]);

  // Mention functionality for description field
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSearchQuery, setMentionSearchQuery] = useState("");
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const quillRef = useRef<ReactQuill>(null);
  const [isCandidateSearching, setIsCandidateSearching] = useState(false);

  const [matchSearchQuery, setMatchSearchQuery] = useState('');
  const [matchSearchResults, setMatchSearchResults] = useState<any[]>([]);
  const [isMatchSearching, setIsMatchSearching] = useState(false);

  const [jobSearchQuery, setJobSearchQuery] = useState('');
  const [jobSearchResults, setJobSearchResults] = useState<any[]>([]);
  const [isJobSearching, setIsJobSearching] = useState(false);

  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const [companySearchResults, setCompanySearchResults] = useState<any[]>([]);
  const [isCompanySearching, setIsCompanySearching] = useState(false);

  // Add entity modals
  const { addCompany } = useAddCompany();
  const { addJob } = useAddJob();
  const [isAddCandidateSelectionOpen, setIsAddCandidateSelectionOpen] = useState(false);
  const [isAddCandidateFormOpen, setIsAddCandidateFormOpen] = useState(false);
  const [candidatePrefillData, setCandidatePrefillData] = useState<CandidateFormData | null>(null);
  const [isAddCompanyOpen, setIsAddCompanyOpen] = useState(false);
  const [isAddJobOpen, setIsAddJobOpen] = useState(false);

  // Register modal with context
  useEffect(() => {
    if (isOpen) {
      openModal();
      // Reset flags when modal opens
      if (isEditing) {
        linkedUsersInitialized.current = false;
        linkedUsersModified.current = false;
      }
    } else {
      closeModal();
      linkedUsersInitialized.current = false;
      linkedUsersModified.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEditing]);

  // Initialize form data when editing or when modal opens
  useEffect(() => {
    if (isOpen) {
      if (isEditing && initialData) {
        // Initialize form with existing task data
        const initialFormState = {
          name: initialData.name || "",
          description: initialData.description || "",
          status: (initialData.status || "todo") as 'todo' | 'inProgress' | 'done',
          priority: (initialData.priority || "medium") as 'low' | 'medium' | 'high' | 'urgent',
          due_date: initialData.due_date ? new Date(initialData.due_date) : null,
          user_id: initialData.user_id || "",
          assigned_to: initialData.assigned_to || ""
        };
        
        setFormData(initialFormState);
        initialFormData.current = initialFormState;
        
        // Initialize relations
        if (initialData.relations) {
          setRelations({
            candidates: initialData.relations.candidates || [],
            matches: initialData.relations.matches || [],
            jobs: initialData.relations.jobs || [],
            companies: initialData.relations.companies || [],
            calendar: initialData.relations.calendar || []
          });
        }
        
        // Initialize custom fields
        if (initialData.custom_fields) {
          setCustomFieldValues(initialData.custom_fields);
          initialCustomFieldValues.current = initialData.custom_fields;
        } else {
          setCustomFieldValues({});
          initialCustomFieldValues.current = {};
        }
        
        // Store pre-linked match info
        if (initialData._prelinkedMatch) {
          setPrelinkedMatch(initialData._prelinkedMatch);
        }
      } else {
        // Creating new task - check if we have initial data with pre-selected relations
        const initialFormState = {
          name: "",
          description: "",
          status: "todo" as 'todo' | 'inProgress' | 'done',
          priority: "medium" as 'low' | 'medium' | 'high' | 'urgent',
          due_date: null as Date | null,
          user_id: "",
          assigned_to: ""
        };
        
        setFormData(initialFormState);
        initialFormData.current = initialFormState;
        
        // Initialize relations from initialData if provided, otherwise reset
        if (initialData?.relations) {
          setRelations({
            candidates: initialData.relations.candidates || [],
            matches: initialData.relations.matches || [],
            jobs: initialData.relations.jobs || [],
            companies: initialData.relations.companies || [],
            calendar: initialData.relations.calendar || []
          });
        } else {
          setRelations({
            candidates: [],
            matches: [],
            jobs: [],
            companies: [],
            calendar: []
          });
        }
        
        setCustomFieldValues({});
        initialCustomFieldValues.current = {};
        
        // Store pre-linked info for display if provided
        setPrelinkedCandidate(initialData?._prelinkedCandidate || null);
        setPrelinkedCompany(initialData?._prelinkedCompany || null);
        setPrelinkedJob(initialData?._prelinkedJob || null);
        setPrelinkedMatch(initialData?._prelinkedMatch || null);
      }
      
      setHasUnsavedChanges(false);
    }
  }, [isOpen, isEditing, initialData]);

  // Initialize user_id and assigned_to with current user when creating new task
  useEffect(() => {
    if (isOpen && !isEditing && userProfile?.id) {
      if (!formData.user_id)
        setFormData(prev => ({ ...prev, user_id: userProfile.id }));
      if (!formData.assigned_to)
        setFormData(prev => ({ ...prev, assigned_to: userProfile.id }));
    }
  }, [isOpen, isEditing, userProfile?.id, formData.user_id, formData.assigned_to]);

  // Sync initial description after Quill formats the content
  useEffect(() => {
    if (isOpen && isEditing && initialData && quillRef.current) {
      const quill = quillRef.current.getEditor();
      if (quill && initialFormData.current) {
        // Wait for Quill to finish formatting
        setTimeout(() => {
          const formattedContent = quill.root.innerHTML;
          // Update both the form data and initial form data to Quill's formatted version
          setFormData(prev => ({ ...prev, description: formattedContent }));
          if (initialFormData.current) {
            initialFormData.current.description = formattedContent;
          }
        }, 100);
      }
    }
  }, [isOpen, isEditing, initialData]);

  // Detect unsaved changes
  useEffect(() => {
    if (!isOpen || !initialFormData.current) return;
    
    // Check form data changes
    const formDataChanged = 
      formData.name !== initialFormData.current.name ||
      formData.description !== initialFormData.current.description ||
      formData.status !== initialFormData.current.status ||
      formData.priority !== initialFormData.current.priority ||
      formData.due_date?.getTime() !== initialFormData.current.due_date?.getTime() ||
      formData.user_id !== initialFormData.current.user_id ||
      formData.assigned_to !== initialFormData.current.assigned_to;
    
    // Check custom fields changes
    const customFieldsChanged = (() => {
      const currentKeys = Object.keys(customFieldValues);
      const initialKeys = Object.keys(initialCustomFieldValues.current);
      
      // Different number of fields
      if (currentKeys.length !== initialKeys.length) return true;
      
      // Check if any value changed
      return currentKeys.some(key => {
        const currentValue = customFieldValues[key];
        const initialValue = initialCustomFieldValues.current[key];
        return currentValue !== initialValue;
      });
    })();
    
    const hasChanges = formDataChanged || linkedUsersModified.current || customFieldsChanged;
    
    setHasUnsavedChanges(hasChanges);
  }, [isOpen, formData, linkedUsersModified, customFieldValues]);

  // Initialize linkedUsers from database when editing (only once when data loads and user hasn't modified)
  useEffect(() => {
    if (
      isOpen && 
      isEditing && 
      !linkedUsersInitialized.current && 
      !linkedUsersModified.current &&
      !linkedUsersLoading && 
      dbLinkedUsers !== undefined
    ) {
      const userIds = dbLinkedUsers.map(u => u.user_id);
      setLinkedUsers(userIds);
      linkedUsersInitialized.current = true;
    }
  }, [isOpen, isEditing, linkedUsersLoading, dbLinkedUsers]);

  // Load custom fields
  useEffect(() => {
    if (!organizationId) return;
    setFieldsLoading(true);
    setFieldsError(null);
    fetchCustomFields(organizationId)
      .then((data: CustomFieldsJSON) => {
        setFields(data.Tasks || []);
      })
      .catch(() => {
        setFieldsError("Failed to load custom fields");
      })
      .finally(() => setFieldsLoading(false));
  }, [organizationId]);

  const handleCustomFieldChange = (name: string, value: any) => {
    setCustomFieldValues((prev) => ({ ...prev, [name]: value }));
  };

  // Candidate search functionality
  const [candidates, setCandidates] = useState<Array<{id: string, name: string, email: string}>>([]);
  
  // Matches search functionality
  const [matches, setMatches] = useState<Array<{id: string, candidate_name: string, job_title: string, company_name: string}>>([]);
  
  // Jobs search functionality
  const [jobs, setJobs] = useState<Array<{id: string, title: string, company_name: string}>>([]);
  
  // Companies search functionality
  const [companies, setCompanies] = useState<Array<{id: string, name: string, city: string, country: string}>>([]);
  
  // Load candidates for search
  useEffect(() => {
    if (!isOpen) return;
    const loadCandidates = async () => {
      try {
        const { data, error } = await supabase
          .from('candidates')
          .select('id, name, email')
          .order('created_at', { ascending: false });

        if (error) throw error;

        const formattedCandidates = (data || []).map((candidate: any) => ({
          id: candidate.id,
          name: candidate.name || candidate.email || 'Unknown',
          email: candidate.email || ''
        }));

        setCandidates(formattedCandidates);
      } catch (error) {
        console.error('Error loading candidates:', error);
      }
    };

    loadCandidates();
  }, [isOpen]);
  
  // Load matches for search
  useEffect(() => {
    if (!isOpen) return;
    const loadMatches = async () => {
      try {
        const { data, error } = await supabase
          .from('matches')
          .select(`
            id,
            candidates(name),
            jobs(title, companies(name))
          `)
          .order('created_at', { ascending: false })
          .limit(500);

        if (error) throw error;

        const formattedMatches = (data || []).map((match: any) => ({
          id: match.id,
          candidate_name: match.candidates?.name || 'Unknown Candidate',
          job_title: match.jobs?.title || 'Unknown Job',
          company_name: match.jobs?.companies?.name || 'Unknown Company'
        }));

        setMatches(formattedMatches);
      } catch (error) {
        console.error('Error loading matches:', error);
      }
    };

    loadMatches();
  }, [isOpen]);
  
  // Load jobs for search
  useEffect(() => {
    if (!isOpen) return;
    const loadJobs = async () => {
      try {
        const { data, error } = await supabase
          .from('jobs')
          .select(`
            id,
            title,
            companies(name)
          `)
          .order('created_at', { ascending: false });

        if (error) throw error;

        const formattedJobs = (data || []).map((job: any) => ({
          id: job.id,
          title: job.title || 'Unknown Job',
          company_name: job.companies?.name || 'Unknown Company'
        }));

        setJobs(formattedJobs);
      } catch (error) {
        console.error('Error loading jobs:', error);
      }
    };

    loadJobs();
  }, [isOpen]);

  // Load companies for search
  useEffect(() => {
    if (!isOpen) return;
    const loadCompanies = async () => {
      try {
        const { data, error } = await supabase
          .from('companies')
          .select(`
            id,
            name,
            city,
            country
          `)
          .order('created_at', { ascending: false });

        if (error) throw error;

        const formattedCompanies = (data || []).map((company: any) => ({
          id: company.id,
          name: company.name || 'Unknown Company',
          city: company.city || '',
          country: company.country || ''
        }));

        setCompanies(formattedCompanies);
      } catch (error) {
        console.error('Error loading companies:', error);
      }
    };

    loadCompanies();
  }, [isOpen]);
  
  const handleCandidateSearch = (query: string) => {
    setCandidateSearchQuery(query);
    if (!query.trim()) {
      setCandidateSearchResults([]);
      return;
    }
    
    setIsCandidateSearching(true);
    const filtered = candidates.filter(candidate => {
      const searchText = `${candidate.name} ${candidate.email}`.toLowerCase();
      const searchWords = query.toLowerCase().trim().split(/\s+/);
      return searchWords.every(word => searchText.includes(word));
    }).slice(0, 5);
    
    setCandidateSearchResults(filtered.map(c => ({
      id: c.id,
      name: c.name || c.email || 'Unknown'
    })));
    setIsCandidateSearching(false);
  };

  const handleAddCandidate = (candidate: {id: string, name: string}) => {
    if (!relations.candidates.includes(candidate.id)) {
      setRelations(prev => ({
        ...prev,
        candidates: [...prev.candidates, candidate.id]
      }));
    }
    setCandidateSearchQuery('');
    setCandidateSearchResults([]);
  };

  const handleRemoveCandidate = (candidateId: string) => {
    setRelations(prev => ({
      ...prev,
      candidates: prev.candidates.filter(id => id !== candidateId)
    }));
  };

  const handleMatchSearch = (query: string) => {
    setMatchSearchQuery(query);
    if (!query.trim()) {
      setMatchSearchResults([]);
      return;
    }
    
    setIsMatchSearching(true);
    const filtered = matches.filter(match => {
      const searchText = `${match.candidate_name} ${match.job_title} ${match.company_name}`.toLowerCase();
      const searchWords = query.toLowerCase().trim().split(/\s+/);
      return searchWords.every(word => searchText.includes(word));
    }).slice(0, 5);
    
    setMatchSearchResults(filtered.map(m => ({
      id: m.id,
      name: `${m.candidate_name} - ${m.job_title} (${m.company_name})`
    })));
    setIsMatchSearching(false);
  };

  const handleAddMatch = (match: {id: string, name: string}) => {
    if (!relations.matches.includes(match.id)) {
      setRelations(prev => ({
        ...prev,
        matches: [...prev.matches, match.id]
      }));
    }
    setMatchSearchQuery('');
    setMatchSearchResults([]);
  };

  const handleRemoveMatch = (matchId: string) => {
    setRelations(prev => ({
      ...prev,
      matches: prev.matches.filter(id => id !== matchId)
    }));
  };

  const handleJobSearch = (query: string) => {
    setJobSearchQuery(query);
    if (!query.trim()) {
      setJobSearchResults([]);
      return;
    }
    
    setIsJobSearching(true);
    const filtered = jobs.filter(job => {
      const searchText = `${job.title} ${job.company_name}`.toLowerCase();
      const searchWords = query.toLowerCase().trim().split(/\s+/);
      return searchWords.every(word => searchText.includes(word));
    }).slice(0, 5);
    
    setJobSearchResults(filtered.map(j => ({
      id: j.id,
      name: `${j.title} (${j.company_name})`
    })));
    setIsJobSearching(false);
  };

  const handleAddJob = (job: {id: string, name: string}) => {
    if (!relations.jobs.includes(job.id)) {
      setRelations(prev => ({
        ...prev,
        jobs: [...prev.jobs, job.id]
      }));
    }
    setJobSearchQuery('');
    setJobSearchResults([]);
  };

  const handleRemoveJob = (jobId: string) => {
    setRelations(prev => ({
      ...prev,
      jobs: prev.jobs.filter(id => id !== jobId)
    }));
  };

  const handleCompanySearch = (query: string) => {
    setCompanySearchQuery(query);
    if (!query.trim()) {
      setCompanySearchResults([]);
      return;
    }
    
    setIsCompanySearching(true);
    const filtered = companies.filter(company => {
      const searchText = `${company.name} ${company.city} ${company.country}`.toLowerCase();
      const searchWords = query.toLowerCase().trim().split(/\s+/);
      return searchWords.every(word => searchText.includes(word));
    }).slice(0, 5);
    
    setCompanySearchResults(filtered.map(c => ({
      id: c.id,
      name: c.city && c.country ? `${c.name} (${c.city}, ${c.country})` : c.name
    })));
    setIsCompanySearching(false);
  };

  const handleAddCompany = (company: {id: string, name: string}) => {
    if (!relations.companies.includes(company.id)) {
      setRelations(prev => ({
        ...prev,
        companies: [...prev.companies, company.id]
      }));
    }
    setCompanySearchQuery('');
    setCompanySearchResults([]);
  };

  const handleRemoveCompany = (companyId: string) => {
    setRelations(prev => ({
      ...prev,
      companies: prev.companies.filter(id => id !== companyId)
    }));
  };

  // Handle description change with mention detection
  const handleDescriptionChange = (value: string) => {
    setFormData(prev => ({ ...prev, description: value }));

    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const selection = quill.getSelection();
    if (!selection) return;

    const cursorPosition = selection.index;
    const text = quill.getText(0, cursorPosition);
    const lastAtSymbol = text.lastIndexOf('@');

    if (lastAtSymbol !== -1 && cursorPosition > lastAtSymbol) {
      const textAfterAt = text.substring(lastAtSymbol + 1, cursorPosition);
      
      // Check if there's a space after @
      if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
        setMentionSearchQuery(textAfterAt);
        setMentionStartIndex(lastAtSymbol);
        
        // Get the position of the cursor relative to the editor
        const bounds = quill.getBounds(cursorPosition);
        const editorElement = quill.root.parentElement;
        if (editorElement) {
          const editorRect = editorElement.getBoundingClientRect();
          const dropdownHeight = 240; // max-h-60 = 240px
          const spaceBelow = editorRect.height - bounds.top - bounds.height;
          const spaceAbove = bounds.top;
          
          // Position dropdown above if not enough space below
          let top;
          if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
            // Position above the current line
            top = bounds.top - dropdownHeight - 5;
          } else {
            // Position below the current line
            top = bounds.top + bounds.height + 5;
          }
          
          setMentionPosition({
            top: top,
            left: bounds.left
          });
        }
        setShowMentionDropdown(true);
      } else {
        setShowMentionDropdown(false);
      }
    } else {
      setShowMentionDropdown(false);
    }
  };

  // Handle user selection from mention dropdown
  const handleUserSelect = (user: { id: string; first_name: string | null; last_name: string | null; email: string }) => {
    const quill = quillRef.current?.getEditor();
    if (!quill || mentionStartIndex === -1) return;

    const selection = quill.getSelection();
    if (!selection) return;

    const userName = user.first_name && user.last_name 
      ? `${user.first_name} ${user.last_name}` 
      : user.first_name || user.last_name || user.email;

    // Delete the @ and search text
    quill.deleteText(mentionStartIndex, selection.index - mentionStartIndex);

    // Insert the mention text with custom format
    const mentionLength = userName.length + 1; // +1 for @
    quill.insertText(mentionStartIndex, `@${userName}`, {
      mention: user.id,
      color: '#5a7ebf',
      bold: true
    });

    // Insert a space after with no formatting
    quill.insertText(mentionStartIndex + mentionLength, ' ');

    // Set cursor position after the mention and space
    setTimeout(() => {
      quill.setSelection(mentionStartIndex + mentionLength + 1, 0);
      // Clear formatting for next text
      quill.format('mention', false);
      quill.format('color', false);
      quill.format('bold', false);
    }, 0);

    setShowMentionDropdown(false);
    setMentionSearchQuery("");
    setMentionStartIndex(-1);

    // Update content
    setFormData(prev => ({ ...prev, description: quill.root.innerHTML }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const taskData = {
        name: formData.name,
        description: formData.description || undefined,
        status: formData.status,
        priority: formData.priority,
        due_date: formData.due_date?.toISOString(),
        user_id: formData.user_id || undefined,
        assigned_to: formData.assigned_to || undefined,
        custom_fields: fields.length > 0 ? customFieldValues : undefined,
        relations: {
          candidates: relations.candidates,
          matches: relations.matches,
          jobs: relations.jobs,
          companies: relations.companies,
          calendar: relations.calendar
        },
        linkedUsers: linkedUsers
      };
      
      if (isEditing && initialData) {
        await updateTaskMutation.mutateAsync({
          id: initialData.id,
          ...taskData
        });
      } else {
        await createTaskMutation.mutateAsync(taskData);
      }
      
      setHasUnsavedChanges(false);
      onSubmit?.();
      onClose();
    } catch (error) {
      console.error('Failed to save task:', error);
    }
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowConfirmDialog(true);
      return;
    }
    
    performClose();
  };

  const performClose = () => {
    setFormData({
      name: "",
      description: "",
      status: "todo",
      priority: "medium",
      due_date: null,
      user_id: "",
      assigned_to: ""
    });
    setRelations({
      candidates: [],
      matches: [],
      jobs: [],
      companies: [],
      calendar: []
    });
    setCustomFieldValues({});
    setActiveTab('general');
    setMobileTab('general');
    setCandidateSearchQuery('');
    setCandidateSearchResults([]);
    setMatchSearchQuery('');
    setMatchSearchResults([]);
    setJobSearchQuery('');
    setJobSearchResults([]);
    setCompanySearchQuery('');
    setCompanySearchResults([]);
    setLinkedUsers([]);
    setShowMentionDropdown(false);
    setMentionSearchQuery("");
    setMentionStartIndex(-1);
    setHasUnsavedChanges(false);
    setShowConfirmDialog(false);
    initialFormData.current = null;
    initialCustomFieldValues.current = {};
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget)
      handleClose();
  };

  const handleAddCandidateManual = () => {
    setIsAddCandidateSelectionOpen(false);
    setCandidatePrefillData(null);
    setIsAddCandidateFormOpen(true);
  };

  const handleAddCandidateCVUpload = (formData: CandidateFormData) => {
    setIsAddCandidateSelectionOpen(false);
    setCandidatePrefillData(formData);
    setIsAddCandidateFormOpen(true);
  };

  const handleCandidateAdded = (_newCandidate: Candidate) => {
    setIsAddCandidateFormOpen(false);
    setCandidatePrefillData(null);
  };

  const handleCompanySubmit = async (companyData: any) => {
    try {
      const success = await addCompany(companyData);
      if (success) {
        setIsAddCompanyOpen(false);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  const handleCreateJob = async (jobData: any) => {
    try {
      const jobId = await addJob(jobData);
      if (jobId) {
        setIsAddJobOpen(false);
        return jobId;
      }
      return undefined;
    } catch {
      return undefined;
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in p-2 sm:p-4"
        onClick={handleBackdropClick}
      >
        <div className="bg-white rounded-sm shadow-2xl w-full max-w-6xl max-h-[95vh] flex flex-col animate-slide-in border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="flex justify-between items-center p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-sm flex items-center justify-center shadow-sm">
                <CheckSquare2 className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {isEditing ? t("tasks.form.edit", "Edit Task") : t("tasks.form.create", "Create Task")}
                </h2>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {customHeaderActions}
              <button
                type="button"
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-sm hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

                                    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
                    {/* Mobile Tab Navigation */}
                    <div className="xl:hidden border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex-shrink-0">
                      <div className="flex">
                        <button
                          type="button"
                          onClick={() => setMobileTab('general')}
                          className={cn(
                            "flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors",
                            mobileTab === 'general'
                              ? "border-blue-500 text-blue-500 bg-blue-50"
                              : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                          )}
                        >
                          <div className="flex items-center justify-center gap-2">
                            <CheckSquare2 className="h-4 w-4" />
                            {isEditing ? t("tasks.form.edit", "Edit Task") : t("tasks.form.create", "Create Task")}
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setMobileTab('relations')}
                          className={cn(
                            "flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors",
                            mobileTab === 'relations'
                              ? "border-blue-500 text-blue-500 bg-blue-50"
                              : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                          )}
                        >
                          <div className="flex items-center justify-center gap-2">
                            <Link className="h-4 w-4" />
                            {t("tasks.relations.title", "Relations")}
                          </div>
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-col xl:flex-row flex-1 min-h-0 overflow-hidden">
                        {/* Main Content - Left Side */}
                        <div className={cn(
                          "flex-1 border-r border-gray-200 dark:border-gray-700 min-h-0",
                          "xl:block",
                          mobileTab === 'general' ? "block" : "hidden"
                        )}>
                                            <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'general' | 'custom-fields' | 'history')} className="w-full h-full flex flex-col">
                {(fields.length > 0 || isEditing) && (
                  <TabsList className="mb-4">
                    <TabsTrigger value="general">
                      <CheckSquare2 className="h-4 w-4 mr-2" />
                      {t("tasks.tabs.general", "General")}
                    </TabsTrigger>
                    {fields.length > 0 && (
                      <TabsTrigger value="custom-fields">
                        <FileText className="h-4 w-4 mr-2" />
                        {t("tasks.tabs.customFields", "Custom Fields")}
                      </TabsTrigger>
                    )}
                    {isEditing && initialData?.id && (
                      <TabsTrigger value="history">
                        <History className="h-4 w-4 mr-2" />
                        {t("tasks.tabs.history", "History")}
                      </TabsTrigger>
                    )}
                  </TabsList>
                )}
                  
                  <TabsContent value="general" className="flex-1 overflow-y-auto">
                    <div className="p-4 space-y-5">
                      {/* Name Field */}
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t("tasks.form.name", "Name")} *
                        </Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder={t("tasks.form.namePlaceholder", "Enter task name")}
                          className="h-11 text-base"
                          required
                        />

                      </div>

                {/* Description Field */}
                <div className="space-y-2">
                  <Label htmlFor="description" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t("tasks.form.description", "Description")} (Type @ to mention a user)
                  </Label>
                  <div className="min-h-[120px] relative">
                    <ReactQuill
                      ref={quillRef}
                      id="description"
                      placeholder={t("tasks.form.descriptionPlaceholder", "Enter task description (optional)... Type @ to mention someone")}
                      value={formData.description}
                      onChange={handleDescriptionChange}
                      modules={{
                        toolbar: [
                          ['bold', 'italic', 'underline', 'strike'],
                          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                          ['link'],
                          ['clean']
                        ],
                      }}
                      formats={[
                        'bold', 'italic', 'underline', 'strike',
                        'list', 'bullet',
                        'link',
                        'color',
                        'mention'
                      ]}
                      className="h-[120px] mb-12"
                    />
                    {showMentionDropdown && (
                      <MentionDropdown
                        searchQuery={mentionSearchQuery}
                        onSelect={handleUserSelect}
                        onClose={() => setShowMentionDropdown(false)}
                        position={mentionPosition}
                      />
                    )}
                  </div>
                </div>

                {/* Status and Priority */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="status" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t("tasks.form.status", "Status")}
                    </Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value as 'todo' | 'inProgress' | 'done' })}>
                      <SelectTrigger className="h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todo">{t("tasks.status.todo", "To Do")}</SelectItem>
                        <SelectItem value="inProgress">{t("tasks.status.inProgress", "In Progress")}</SelectItem>
                        <SelectItem value="done">{t("tasks.status.done", "Done")}</SelectItem>
                        <SelectItem value="cancelled">{t("tasks.status.cancelled", "Cancelled")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="priority" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t("tasks.form.priority", "Priority")}
                    </Label>
                    <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value as 'low' | 'medium' | 'high' | 'urgent' })}>
                      <SelectTrigger className="h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">{t("tasks.priority.low", "Low")}</SelectItem>
                        <SelectItem value="medium">{t("tasks.priority.medium", "Medium")}</SelectItem>
                        <SelectItem value="high">{t("tasks.priority.high", "High")}</SelectItem>
                        <SelectItem value="urgent">{t("tasks.priority.urgent", "Urgent")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Due Date and Assignee */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                  <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t("tasks.form.dueDate", "Due Date")}
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal h-11",
                          !formData.due_date && "text-gray-500 dark:text-gray-400"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.due_date ? format(formData.due_date, "PPP") : t("tasks.form.dueDate", "Due Date")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-white dark:bg-gray-800" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.due_date}
                        onSelect={(date) => setFormData({ ...formData, due_date: date })}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="reporter" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t("tasks.form.reporter", "Reporter")}
                    </Label>
                    <AssigneeSelector
                      value={formData.user_id}
                      onValueChange={(value) => setFormData({ ...formData, user_id: value })}
                      placeholder={t("tasks.form.reporterPlaceholder", "Select reporter")}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="assignedTo" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t("tasks.list.assignedTo", "Assigned To")}
                    </Label>
                    <AssigneeSelector
                      value={formData.assigned_to}
                      onValueChange={(value) => setFormData({ ...formData, assigned_to: value })}
                      placeholder={t("tasks.form.assignedToPlaceholder", "Select assignee")}
                    />
                  </div>
                </div>


              </div>
            </TabsContent>
            
            {fields.length > 0 && (
              <TabsContent value="custom-fields" className="flex-1 overflow-y-auto">
                <div className="p-4 space-y-5">
                  
                  {fieldsLoading && (
                    <div className="text-center py-4">{t("tasks.customFields.loading", "Loading custom fields...")}</div>
                  )}
                  
                  {fieldsError && (
                    <div className="text-red-600 py-4">{fieldsError}</div>
                  )}
                  
                  {!fieldsLoading && !fieldsError && fields.length === 0 && (
                    <div className="text-gray-500 dark:text-gray-400 py-4">{t("tasks.customFields.noFields", "No custom fields configured for tasks.")}</div>
                  )}
                  
                  {!fieldsLoading && !fieldsError && fields.length > 0 && (
                    <div className="space-y-4">
                      {fields.map((field) => {
                        const value = customFieldValues[field.name] ?? "";
                        return (
                          <div key={field.name} className="flex flex-col gap-1">
                            <label className="font-medium text-sm mb-1">{field.name}</label>
                            {field.type === "text" && (
                              <Input
                                value={value}
                                onChange={e => handleCustomFieldChange(field.name, e.target.value)}
                                placeholder={field.name}
                                aria-label={field.name}
                              />
                            )}
                            {field.type === "number" && (
                              <Input
                                type="number"
                                value={value}
                                onChange={e => handleCustomFieldChange(field.name, e.target.value)}
                                placeholder={field.name}
                                aria-label={field.name}
                              />
                            )}
                            {field.type === "boolean" && (
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  checked={!!value}
                                  onCheckedChange={checked => handleCustomFieldChange(field.name, checked)}
                                  id={field.name}
                                  aria-label={field.name}
                                />
                                <label htmlFor={field.name} className="text-sm">{field.name}</label>
                              </div>
                            )}
                            {field.type === "dropdown" && field.options && (
                              <Select
                                value={value}
                                onValueChange={value => handleCustomFieldChange(field.name, value)}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder={field.name} />
                                </SelectTrigger>
                                <SelectContent>
                                  {field.options.map((option) => (
                                    <SelectItem key={option} value={option}>
                                      {option}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </TabsContent>
            )}
            
            {isEditing && initialData?.id && (
              <TabsContent value="history" className="flex-1 overflow-y-auto">
                <div className="p-4">
                  <TaskHistoryTab taskId={initialData.id} />
                </div>
              </TabsContent>
            )}
          </Tabs>
        
        </div>

                                    {/* Relations Sidebar - Right Side */}
                        <div className={cn(
                          "w-full xl:w-80 border-t xl:border-l xl:border-t-0 border-gray-200 dark:border-gray-700 bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 min-h-0 flex flex-col",
                          "xl:block",
                          mobileTab === 'relations' ? "block" : "hidden"
                        )}>
              <div className="p-4 space-y-3 overflow-y-auto" style={{ maxHeight: 'calc(95vh - 200px)' }}>

                {/* Linked Users */}
                <div className="space-y-2 p-2.5 bg-white rounded-sm border border-gray-200 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 bg-purple-100 rounded-sm flex items-center justify-center">
                        <Users className="h-3 w-3 text-purple-600" />
                      </div>
                      <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{t("tasks.relations.linkedUsers", "Linked Users")}</span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Select onValueChange={(userId) => {
                      if (!linkedUsers.includes(userId)) {
                        setLinkedUsers([...linkedUsers, userId]);
                        linkedUsersModified.current = true;
                      }
                    }}>
                      <SelectTrigger className="h-7 text-xs">
                        <SelectValue placeholder={t("tasks.form.addUser", "Add user...")} />
                      </SelectTrigger>
                      <SelectContent>
                        {users.filter(u => !linkedUsers.includes(u.id)).map(user => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.first_name} {user.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {linkedUsersLoading ? (
                      <p className="text-xs text-gray-500 dark:text-gray-400 italic">{t("common.loading", "Loading...")}</p>
                    ) : linkedUsers.length > 0 ? (
                      <div className="space-y-1">
                        {linkedUsers.map(userId => {
                          const user = users.find(u => u.id === userId);
                          if (!user) return null;
                          return (
                            <div key={userId} className="flex items-center justify-between p-2 text-xs bg-purple-50 rounded-sm">
                              <span className="truncate">{user.first_name} {user.last_name}</span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setLinkedUsers(prev => prev.filter(id => id !== userId));
                                  linkedUsersModified.current = true;
                                }}
                                className="text-purple-600 hover:text-purple-800 transition-colors"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500 dark:text-gray-400 italic">{t("tasks.relations.noLinkedUsers", "No linked users")}</p>
                    )}
                  </div>
                </div>

                {/* Candidates (EntitySearchPopover) */}
                <EntitySearchPopover
                  entityType="candidate"
                  searchQuery={candidateSearchQuery}
                  onSearchChange={handleCandidateSearch}
                  searchResults={candidateSearchResults}
                  isSearching={isCandidateSearching}
                  selectedIds={relations.candidates}
                  onAddItem={item => setRelations(prev => ({ ...prev, candidates: [...prev.candidates, item.id] }))}
                  onRemoveItem={id => setRelations(prev => ({ ...prev, candidates: prev.candidates.filter(cid => cid !== id) }))}
                  placeholder={t("tasks.relations.searchCandidates", "Search candidates...")}
                  icon={<Users className="h-3 w-3" />}
                  iconBgColor="bg-blue-100"
                  iconColor="text-blue-500"
                  resultBgColor="bg-blue-50"
                  resultBgColorDark="dark:bg-blue-900/30"
                  resultHoverColor="hover:bg-blue-100"
                  resultHoverColorDark="dark:hover:bg-blue-900/50"
                  noItemsText={t("tasks.relations.noCandidatesLinked", "No candidates linked")}
                  allAvailableItems={candidates}
                  onCreateNew={() => setIsAddCandidateSelectionOpen(true)}
                />


                {/* Matches (EntitySearchPopover) */}
                <EntitySearchPopover
                  entityType="match"
                  searchQuery={matchSearchQuery}
                  onSearchChange={handleMatchSearch}
                  searchResults={matchSearchResults}
                  isSearching={isMatchSearching}
                  selectedIds={relations.matches}
                  onAddItem={item => setRelations(prev => ({ ...prev, matches: [...prev.matches, item.id] }))}
                  onRemoveItem={id => setRelations(prev => ({ ...prev, matches: prev.matches.filter(mid => mid !== id) }))}
                  placeholder={t("tasks.relations.searchMatches", "Search matches...")}
                  icon={<Puzzle className="h-3 w-3" />}
                  iconBgColor="bg-purple-100"
                  iconBgColorDark="dark:bg-purple-900"
                  iconColor="text-purple-600"
                  iconColorDark="dark:text-purple-400"
                  resultBgColor="bg-purple-50"
                  resultBgColorDark="dark:bg-purple-900/30"
                  resultHoverColor="hover:bg-purple-100"
                  resultHoverColorDark="dark:hover:bg-purple-900/50"
                  noItemsText={t("tasks.relations.noMatchesLinked", "No matches linked")}
                  allAvailableItems={matches.map(m => ({ id: m.id, name: `${m.candidate_name} - ${m.job_title} (${m.company_name})` }))}
                />


                {/* Jobs/Offers (EntitySearchPopover) */}
                <EntitySearchPopover
                  entityType="job"
                  searchQuery={jobSearchQuery}
                  onSearchChange={handleJobSearch}
                  searchResults={jobSearchResults}
                  isSearching={isJobSearching}
                  selectedIds={relations.jobs}
                  onAddItem={item => setRelations(prev => ({ ...prev, jobs: [...prev.jobs, item.id] }))}
                  onRemoveItem={id => setRelations(prev => ({ ...prev, jobs: prev.jobs.filter(jid => jid !== id) }))}
                  placeholder={t("tasks.relations.searchJobs", "Search jobs...")}
                  icon={<ClipboardList className="h-3 w-3" />}
                  iconBgColor="bg-green-100"
                  iconBgColorDark="dark:bg-green-900"
                  iconColor="text-green-600"
                  iconColorDark="dark:text-green-400"
                  resultBgColor="bg-green-50"
                  resultBgColorDark="dark:bg-green-900/30"
                  resultHoverColor="hover:bg-green-100"
                  resultHoverColorDark="dark:hover:bg-green-900/50"
                  noItemsText={t("tasks.relations.noJobsLinked", "No jobs linked")}
                  allAvailableItems={jobs.map(j => ({ id: j.id, name: `${j.title} (${j.company_name})` }))}
                  onCreateNew={() => setIsAddJobOpen(true)}
                />


                {/* Companies (EntitySearchPopover) */}
                <EntitySearchPopover
                  entityType="company"
                  searchQuery={companySearchQuery}
                  onSearchChange={handleCompanySearch}
                  searchResults={companySearchResults}
                  isSearching={isCompanySearching}
                  selectedIds={relations.companies}
                  onAddItem={item => setRelations(prev => ({ ...prev, companies: [...prev.companies, item.id] }))}
                  onRemoveItem={id => setRelations(prev => ({ ...prev, companies: prev.companies.filter(cid => cid !== id) }))}
                  placeholder={t("tasks.relations.searchCompanies", "Search companies...")}
                  icon={<Briefcase className="h-3 w-3" />}
                  allAvailableItems={companies}
                  iconBgColor="bg-orange-100"
                  iconBgColorDark="dark:bg-orange-900"
                  iconColor="text-orange-600"
                  iconColorDark="dark:text-orange-400"
                  resultBgColor="bg-orange-50"
                  resultBgColorDark="dark:bg-orange-900/30"
                  resultHoverColor="hover:bg-orange-100"
                  resultHoverColorDark="dark:hover:bg-orange-900/50"
                  noItemsText={t("tasks.relations.noCompaniesLinked", "No companies linked")}
                  onCreateNew={() => setIsAddCompanyOpen(true)}
                />

                {/* Calendar */}
                <div className="space-y-2 p-2.5 bg-gray-50 rounded-sm border border-gray-200 shadow-sm opacity-50 cursor-not-allowed">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 bg-gray-200 rounded-sm flex items-center justify-center">
                        <CalendarDays className="h-3 w-3 text-gray-400" />
                      </div>
                      <span className="text-sm font-semibold text-gray-500 dark:text-gray-400">{t("tasks.relations.calendar", "Calendar")}</span>
                      <span className="text-xs text-gray-400 dark:text-gray-500 italic">({t("common.availableSoon", "Available soon")})</span>
                    </div>
                  </div>
                  <div className="space-y-1.5 pointer-events-none">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                      <Input
                        placeholder={t("tasks.relations.searchCalendar", "Search calendar events...")}
                        className="h-7 text-xs pl-7 bg-gray-100 dark:bg-gray-700"
                        disabled
                      />
                    </div>
                    <div className="text-xs text-gray-400 text-center py-1.5 bg-gray-100 rounded-sm">
                      {t("tasks.relations.noCalendarLinked", "No calendar events linked")}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>


          
          {/* Footer */}
                      <div className="flex flex-col sm:flex-row justify-end p-3 sm:p-4 lg:p-6 border-t border-gray-200 dark:border-gray-700 gap-2 sm:gap-3 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 flex-shrink-0">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleClose}
                          className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-2 border-gray-300 dark:border-gray-600 hover:border-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm sm:text-base"
                        >
                          {t("common.cancel", "Cancel")}
                        </Button>
                                                  <Button
                            type="submit"
                            disabled={isLoading || !formData.name.trim()}
                            className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-2"
                          >
                            {isLoading ? (isEditing ? t("tasks.form.updating", "Updating...") : t("tasks.form.creating", "Creating...")) : (isEditing ? t("tasks.form.update", "Update Task") : t("tasks.form.create", "Create Task"))}
                          </Button>
                      </div>
        </form>
      </div>
    </div>
    
    <ConfirmationDialog
      isOpen={showConfirmDialog}
      onClose={() => setShowConfirmDialog(false)}
      onConfirm={performClose}
      title={t("common.unsavedChanges", "Unsaved Changes")}
      description={t("common.unsavedChangesMessage", "You have unsaved changes. Are you sure you want to close this without saving?")}
      confirmText={t("common.closeWithoutSaving", "Close Without Saving")}
      cancelText={t("common.cancel", "Cancel")}
      variant="destructive"
      showWarningIcon={true}
    />
    <AddCandidateSelectionModal
      isOpen={isAddCandidateSelectionOpen}
      onClose={() => setIsAddCandidateSelectionOpen(false)}
      onManualCreate={handleAddCandidateManual}
      onCVUpload={handleAddCandidateCVUpload}
    />
    <AddCandidateForm
      isOpen={isAddCandidateFormOpen}
      onClose={() => { setIsAddCandidateFormOpen(false); setCandidatePrefillData(null); }}
      onCandidateAdded={handleCandidateAdded}
      initialFormData={candidatePrefillData}
    />
    <AddCompanyForm
      isOpen={isAddCompanyOpen}
      onClose={() => setIsAddCompanyOpen(false)}
      onSubmit={handleCompanySubmit}
    />
    <JobOffersAddModal
      isOpen={isAddJobOpen}
      onClose={() => setIsAddJobOpen(false)}
      onAddJob={handleCreateJob}
    />
    </>
  );
};
